import type { ChangeEvent } from 'react';
import { Box, Input } from '@chakra-ui/react';
import { Button } from '@kula-ai/components';
import { Plus } from '@kula-ai/icons-core';
import { useShowToast } from '@/hooks';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useUploadAttachmentMutation } from '../../api';
import { useOfferStore } from '../../store';

// Additional files caps (CORE-4807): at most MAX_FILES and MAX_TOTAL_SIZE_MB combined.
const MAX_FILES = 10;
const MAX_TOTAL_SIZE_MB = 15;
const BYTES_PER_MB = 1024 * 1024;

const attachmentBytes = (a: { byte_size?: number; size?: number }) =>
  a?.byte_size ?? a?.size ?? 0;

// Uploads an extra document onto this offer (additional_documents), same behaviour
// as the old footer button — relocated under the document tabs per Figma.
export const AddMoreFilesButton = ({
  offerId,
  variant = 'tertiary',
}: {
  offerId?: number | string;
  variant: string;
}) => {
  const addOfferAttachment = useOfferStore((state) => state.addOfferAttachment);
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const storeOfferId = useOfferStore((state) => state.offerId);
  const toast = useShowToast();
  const { handleAttachmentValidation } = useFileValidation();

  const uploadMutation = useUploadAttachmentMutation({
    onSuccess: (data: { data?: { attachments?: unknown[] } }) => {
      const attachment = data?.data?.attachments?.[0];
      if (attachment) addOfferAttachment(attachment);
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to upload file',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    // Cap the number of additional files: at most MAX_FILES total, which also bounds
    // how many can be picked in one multi-select (CORE-4807).
    const remainingSlots = MAX_FILES - offerAttachments.length;
    if (remainingSlots <= 0) {
      toast({
        title: `You can add up to ${MAX_FILES} files`,
        status: 'error',
      });
      return;
    }
    if (files.length > remainingSlots) {
      toast({
        title: `You can add up to ${MAX_FILES} files`,
        description: `You can add ${remainingSlots} more file${
          remainingSlots === 1 ? '' : 's'
        }.`,
        status: 'error',
      });
      return;
    }

    // Reject the whole selection if any file is damaged / unsupported / empty.
    const isValid = await handleAttachmentValidation({ files });
    if (!isValid) return;

    // Cap the combined size of all additional files (existing + newly selected).
    const existingBytes = offerAttachments.reduce(
      (total, a) => total + attachmentBytes(a),
      0
    );
    const newBytes = files.reduce((total, f) => total + f.size, 0);
    if ((existingBytes + newBytes) / BYTES_PER_MB > MAX_TOTAL_SIZE_MB) {
      toast({
        title: 'Total file size exceeds limit',
        description: `All additional files together must be under ${MAX_TOTAL_SIZE_MB} MB.`,
        status: 'error',
      });
      return;
    }

    // The upload endpoint takes one file per request, so fire one per selected file.
    const id = offerId || storeOfferId || 0;
    files.forEach((file) => uploadMutation.mutate({ id, file }));
  };

  return (
    <Box position="relative" w="186px" cursor="pointer">
      <Input
        type="file"
        multiple
        onChange={handleChange}
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        opacity={0}
        cursor="pointer"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        zIndex={1}
        // Suppress the browser's native "No file chosen" tooltip on the overlaid input.
        title=""
      />
      <Button
        variant={variant}
        size="regular"
        isLoading={uploadMutation.isPending}
        leftElement={<Plus size={16} />}
        style={{ width: '100%' }}
      >
        Add more files
      </Button>
    </Box>
  );
};
